import { getAccessToken } from '../../../../../auth';
import { dispatchSessionExpiredIfUnauthorized, isSessionExpired } from '../../../../../auth/sessionExpiry';
import config from '../../../../../../assets/config';
import type { CfNexusStartContext } from '../../../../../cloudflare-integration';
import { NexusEntryPoint, NexusIntegrationFlow } from './types';

/**
 * Nexus API Client
 * Provides functions to interact with the Nexus backend API
 */

export interface StartConversationResponse {
  thread_id: string;
  message: string;
}

export interface StartConversationApiPayload {
  nexus_entry_point?: NexusEntryPoint;
  integration_flow?: NexusIntegrationFlow;
}

export interface SendMessageResponse {
  thread_id: string;
  message: string;
}

/**
 * API error response structure from backend
 */
interface ApiErrorResponse {
  message?: string | string[];
}

/**
 * Conversation history message
 */
export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  image_url?: string;
  timestamp?: string;
}

/**
 * Response for fetching conversation history
 */
export interface ConversationHistoryResponse {
  thread_id: string;
  messages: ConversationMessage[];
  message_count: number;
}

/**
 * API payload for sending a message
 * Includes thread_id for the API call
 */
export interface SendMessageApiPayload {
  thread_id: string;
  message: string;
  screenshot?: File;
}

/**
 * Error types for Nexus API
 */
export enum NexusErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  UNAUTHORIZED = 'UNAUTHORIZED',
  BAD_REQUEST = 'BAD_REQUEST',
  INVALID_THREAD = 'INVALID_THREAD',
  SCREENSHOT_TOO_LARGE = 'SCREENSHOT_TOO_LARGE',
  INVALID_SCREENSHOT_TYPE = 'INVALID_SCREENSHOT_TYPE',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export class NexusApiError extends Error {
  constructor(
    message: string,
    public type: NexusErrorType,
    public statusCode?: number,
    public retryable: boolean = false
  ) {
    super(message);
    this.name = 'NexusApiError';
  }
}

/**
 * Maximum allowed screenshot file size — 1 MB metric (1,000,000 bytes).
 *
 * The nginx ingress `proxy-body-size` default is 1 MiB (1,048,576 bytes)
 * and applies to the *entire* multipart request body, not just the raw
 * file. Using a metric MB here leaves ~48 KB of headroom for boundaries,
 * per-part headers, the thread_id field, and the message field so a
 * file at the limit cannot push the serialized body past the ingress cap.
 */
const MAX_SCREENSHOT_SIZE_BYTES = 1000 * 1000;
const MAX_SCREENSHOT_SIZE_LABEL = '1MB';

/**
 * Allowed MIME types for screenshot uploads
 */
const ALLOWED_SCREENSHOT_MIME_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

/**
 * Request timeout in milliseconds (30 seconds)
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * Validate screenshot file before upload
 */
function validateScreenshot(file: File): void {
  if (!ALLOWED_SCREENSHOT_MIME_TYPES.includes(file.type)) {
    throw new NexusApiError(
      `Invalid file type: ${file.type}. Please use PNG, JPG, JPEG, GIF, or WebP.`,
      NexusErrorType.INVALID_SCREENSHOT_TYPE,
      undefined,
      false
    );
  }

  if (file.size > MAX_SCREENSHOT_SIZE_BYTES) {
    throw new NexusApiError(
      `File is too large (${(file.size / 1000 / 1000).toFixed(2)} MB). Maximum size is ${MAX_SCREENSHOT_SIZE_LABEL}.`,
      NexusErrorType.SCREENSHOT_TOO_LARGE,
      undefined,
      false
    );
  }
}

/**
 * Create headers for API requests
 * Uses standard Authorization: Bearer <token> format for JWT authentication
 */
function createHeaders(): HeadersInit {
  const accessToken = getAccessToken();
  const headers: HeadersInit = {};

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
}

/**
 * Fetch with timeout
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> {
  // Once the session is expired the unified modal is already showing; fail fast
  // instead of firing a request that is guaranteed to 401.
  if (isSessionExpired()) {
    throw new NexusApiError(
      'Your session has expired. Please sign in again.',
      NexusErrorType.UNAUTHORIZED,
      401,
      false
    );
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    const err = error as { name?: string };
    if (err.name === 'AbortError') {
      throw new NexusApiError(
        'Request timed out. Please try again.',
        NexusErrorType.TIMEOUT,
        undefined,
        true
      );
    }
    throw error;
  }
}

/**
 * Handle API error responses
 * Ensures errorMessage is always a string by handling NestJS validation arrays
 */
function handleErrorResponse(status: number, errorData: unknown): NexusApiError {
  const error = errorData as ApiErrorResponse;
  let errorMessage: string;
  if (Array.isArray(error?.message)) {
    errorMessage = error.message.join(', ');
  } else if (typeof error?.message === 'string') {
    errorMessage = error.message;
  } else {
    errorMessage = 'An error occurred';
  }

  switch (status) {
    case 400:
      if (errorMessage.toLowerCase().includes('thread')) {
        return new NexusApiError(
          'Invalid conversation. Starting a new one...',
          NexusErrorType.INVALID_THREAD,
          400,
          false
        );
      }
      return new NexusApiError(
        errorMessage,
        NexusErrorType.BAD_REQUEST,
        400,
        false
      );

    case 401:
      return new NexusApiError(
        'Authentication failed. Please refresh the page.',
        NexusErrorType.UNAUTHORIZED,
        401,
        false
      );

    case 413:
      // Edge infra (e.g. nginx ingress) rejected the body before it reached
      // the API. The client validates screenshot size before sending, but
      // handle 413 explicitly so a server/edge limit mismatch still surfaces
      // a clean "too large" message instead of a generic retried error.
      return new NexusApiError(
        `Screenshot is too large. Maximum size is ${MAX_SCREENSHOT_SIZE_LABEL}.`,
        NexusErrorType.SCREENSHOT_TOO_LARGE,
        413,
        false
      );

    case 422:
      // Unprocessable Entity - typically invalid thread ID from LangGraph
      return new NexusApiError(
        'Invalid conversation. Please start a new one.',
        NexusErrorType.INVALID_THREAD,
        422,
        false
      );

    case 429:
      return new NexusApiError(
        'Too many requests. Please wait a moment before trying again.',
        NexusErrorType.SERVICE_UNAVAILABLE,
        429,
        true
      );

    case 503:
      return new NexusApiError(
        'Nexus is temporarily unavailable. Please try again in a few moments or contact support if the issue persists.',
        NexusErrorType.SERVICE_UNAVAILABLE,
        503,
        true
      );

    case 504:
      return new NexusApiError(
        'Request timed out. Please try again.',
        NexusErrorType.TIMEOUT,
        504,
        true
      );

    default:
      if (status >= 500) {
        return new NexusApiError(
          'Server error occurred. Please try again or contact support.',
          NexusErrorType.SERVER_ERROR,
          status,
          true
        );
      }
      return new NexusApiError(
        errorMessage,
        NexusErrorType.UNKNOWN_ERROR,
        status,
        false
      );
  }
}

/**
 * Throw a mapped NexusApiError for any non-OK response, and — for a 401 — open
 * the unified SessionExpiredModal (same guard the other HTTP clients use) so an
 * expired session no longer surfaces per-page "refresh the page" copy.
 */
async function throwOnErrorResponse(response: Response): Promise<void> {
  if (response.ok) {
    return;
  }

  dispatchSessionExpiredIfUnauthorized(response.status);
  const errorData = await response.json().catch(() => ({}));
  throw handleErrorResponse(response.status, errorData);
}

function sessionRequestBody(
  payload: StartConversationApiPayload | undefined,
  cfContext: CfNexusStartContext | undefined
): Record<string, string> {
  return {
    ...(payload?.nexus_entry_point ? { nexus_entry_point: payload.nexus_entry_point } : {}),
    ...(payload?.integration_flow ? { integration_flow: payload.integration_flow } : {}),
    ...(cfContext?.domain ? { domain: cfContext.domain } : {}),
    ...(cfContext?.fromStep ? { from_step: cfContext.fromStep } : {}),
    ...(cfContext?.errorShown ? { error_shown: cfContext.errorShown } : {}),
  };
}

export async function reportResumedSession(
  threadId: string,
  payload: StartConversationApiPayload | undefined,
  cfContext: CfNexusStartContext | undefined
): Promise<void> {
  const url = `${config.apiUrl.restServer}/v3/nexus/thread/${threadId}/resumed`;

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      ...createHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sessionRequestBody(payload, cfContext)),
  });

  await throwOnErrorResponse(response);
}

/**
 * Start a new Nexus conversation
 * Calls POST /v3/nexus/start
 */
export async function startConversation(
  payload?: StartConversationApiPayload,
  cfContext?: CfNexusStartContext
): Promise<StartConversationResponse> {
  const url = `${config.apiUrl.restServer}/v3/nexus/start`;

  try {
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        ...createHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sessionRequestBody(payload, cfContext)),
    });

    await throwOnErrorResponse(response);

    const data: StartConversationResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof NexusApiError) {
      throw error;
    }

    const err = error as { name?: string; message?: string };
    if (err.name === 'TypeError' && err.message?.includes('fetch')) {
      throw new NexusApiError(
        'Network error. Please check your connection and try again.',
        NexusErrorType.NETWORK_ERROR,
        undefined,
        true
      );
    }

    if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
      console.error('[Nexus] Failed to start conversation:', error);
    }
    throw new NexusApiError(
      'Failed to start conversation. Please try again.',
      NexusErrorType.UNKNOWN_ERROR,
      undefined,
      true
    );
  }
}

/**
 * Send a message to Nexus
 * Calls POST /v3/nexus/message
 * Supports optional screenshot upload via multipart/form-data
 */
export async function sendMessage(
  payload: SendMessageApiPayload
): Promise<SendMessageResponse> {
  const { thread_id, message, screenshot } = payload;
  const url = `${config.apiUrl.restServer}/v3/nexus/message`;

  if (screenshot) {
    validateScreenshot(screenshot);
  }

  try {
    const formData = new FormData();
    formData.append('thread_id', thread_id);
    formData.append('message', message);

    if (screenshot) {
      formData.append('screenshot', screenshot);
    }

    const headers = createHeaders();
    // Note: Don't set Content-Type header - browser will set it with boundary for FormData

    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    await throwOnErrorResponse(response);

    const data: SendMessageResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof NexusApiError) {
      throw error;
    }

    const err = error as { name?: string; message?: string };
    if (err.name === 'TypeError' && err.message?.includes('fetch')) {
      throw new NexusApiError(
        'Network error. Please check your connection and try again.',
        NexusErrorType.NETWORK_ERROR,
        undefined,
        true
      );
    }

    if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
      console.error('[Nexus] Failed to send message:', error);
    }
    throw new NexusApiError(
      'Failed to send message. Please try again.',
      NexusErrorType.UNKNOWN_ERROR,
      undefined,
      true
    );
  }
}

/**
 * Fetch conversation history for a thread
 * Calls GET /v3/nexus/thread/:thread_id/history
 * @param threadId - The conversation thread ID
 * @returns Conversation history with all messages
 */
export async function fetchConversationHistory(
  threadId: string
): Promise<ConversationHistoryResponse> {
  const url = `${config.apiUrl.restServer}/v3/nexus/thread/${threadId}/history`;

  try {
    const headers = createHeaders();

    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers,
    });

    await throwOnErrorResponse(response);

    const data: ConversationHistoryResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof NexusApiError) {
      throw error;
    }

    const err = error as { name?: string; message?: string };
    if (err.name === 'TypeError' && err.message?.includes('fetch')) {
      throw new NexusApiError(
        'Network error. Please check your connection and try again.',
        NexusErrorType.NETWORK_ERROR,
        undefined,
        true
      );
    }

    if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
      console.error('[Nexus] Failed to fetch conversation history:', error);
    }
    throw new NexusApiError(
      'Failed to fetch conversation history. Please try again.',
      NexusErrorType.UNKNOWN_ERROR,
      undefined,
      true
    );
  }
}

/**
 * Retry a function with exponential backoff
 * Used for retryable errors (network issues, timeouts, service unavailable)
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelayMs = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      lastError = error as Error;

      // Don't retry for non-retryable errors
      if (error instanceof NexusApiError && !error.retryable) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate exponential backoff delay: 1s, 2s, 4s
      const delayMs = initialDelayMs * Math.pow(2, attempt);

      if (process.env.REACT_APP_NEXUS_DEBUG_MODE === 'true') {
        console.log(`[Nexus] Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
      }
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}
